import api from './client'

// Tenant/project document store backing the transaction image/document
// upload widgets. Distinct from the collaborative document pages in
// ./documents.ts and from the page asset library in AssetPicker.

export interface StoredDocument {
  id: string
  name: string
  file: string
  mime?: string
  size?: number
  createdAt?: number
  url: string
}

export async function listDocuments(project: string): Promise<StoredDocument[]> {
  try {
    const r = await api.get<{ success: boolean; documents: StoredDocument[] }>(
      '/designers/documents/list',
      { params: { project } },
    )
    return r.data?.documents ?? []
  } catch {
    return []
  }
}

// Optional doc-type / record-id segmentation. Transaction forms omit it (the
// server finalizes the folder on save); the smart-page upload block supplies
// a resolved document type + transaction id so the file is filed immediately.
export interface UploadMeta {
  docType?: string
  recordId?: string
  /** The value being replaced — lets a repository-backed column file the
   *  upload as the next VERSION of the same document. */
  replaces?: string
}

export async function uploadDocument(
  project: string,
  file: File,
  meta?: UploadMeta,
): Promise<StoredDocument> {
  const fd = new FormData()
  fd.append('project', project)
  fd.append('file', file)
  if (meta?.docType) fd.append('doc_type', meta.docType)
  if (meta?.recordId) fd.append('record_id', meta.recordId)
  if (meta?.replaces) fd.append('replaces', meta.replaces)
  const r = await api.post<{ success: boolean; document: StoredDocument }>(
    '/designers/documents/upload',
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120_000 },
  )
  if (!r.data?.document) throw new Error('Upload failed')
  return r.data.document
}

export async function deleteDocument(project: string, id: string): Promise<boolean> {
  try {
    const r = await api.post<{ success: boolean; removed: boolean }>(
      '/designers/documents/delete',
      { project, id },
    )
    return !!r.data?.removed
  } catch {
    return false
  }
}
